'use client';

import React, { createContext, useContext, useCallback, useState } from 'react';
import { CreateOrderDto } from '@/core';
import { useCartState } from '@/core/application/providers/StateMachineProvider';
import { useOrderWorkflow } from '@/core/application/hooks/useOrderWorkflow';
import { OrderTypeUtils } from '@/core/application/adapters/OrderAdapter';
// CartSaved type no longer needed with adapter layer
import type { OrderReadyToSubmit } from '@/components/order/order.types';

// Transitional Order State - bridges to state machine architecture
interface OrderState {
  dietaryData: CreateOrderDto['dietaryRequirements'] | null;
  deliveryData: CreateOrderDto['deliveryInfo'] | null;
  isLoading: boolean;
  error: string | null;
  step: 'cart' | 'dietary' | 'delivery' | 'summary' | 'submitted';
  submittedOrder: { orderId: string; timestamp: number } | null;
}

interface OrderContextType {
  state: OrderState;
  setDietaryData: (data: CreateOrderDto['dietaryRequirements']) => void;
  setDeliveryData: (data: CreateOrderDto['deliveryInfo']) => void;
  setStep: (step: OrderState['step']) => void;
  submitOrder: () => Promise<void>;
  createOrderToSubmit: () => OrderReadyToSubmit | null;
  resetOrder: () => void;
  isReadyToSubmit: () => boolean;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

export const useCleanOrder = useOrder;

export const OrderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const workflow = useOrderWorkflow();
  const { items: cartItems } = useCartState();
  const [localState, setLocalState] = useState({
    dietaryData: null as CreateOrderDto['dietaryRequirements'] | null,
    deliveryData: null as CreateOrderDto['deliveryInfo'] | null,
    step: 'cart' as OrderState['step'],
  });

  // Bridge state machine to legacy interface
  const state: OrderState = {
    dietaryData: localState.dietaryData,
    deliveryData: localState.deliveryData,
    isLoading: workflow.isLoading,
    error: workflow.context.error,
    step: localState.step,
    submittedOrder: workflow.context.orderId
      ? {
          orderId: workflow.context.orderId,
          timestamp: workflow.context.timestamp || Date.now(),
        }
      : null,
  };

  const setDietaryData = useCallback((data: CreateOrderDto['dietaryRequirements']) => {
    setLocalState((prev) => ({ ...prev, dietaryData: data }));
  }, []);

  const setDeliveryData = useCallback((data: CreateOrderDto['deliveryInfo']) => {
    setLocalState((prev) => ({ ...prev, deliveryData: data }));
  }, []);

  const setStep = useCallback((step: OrderState['step']) => {
    setLocalState((prev) => ({ ...prev, step }));
  }, []);

  const resetOrder = useCallback(() => {
    setLocalState({
      dietaryData: null,
      deliveryData: null,
      step: 'cart',
    });
    workflow.reset();
  }, [workflow]);

  const isReadyToSubmit = useCallback(() => {
    return !!(
      cartItems.length > 0 &&
      localState.dietaryData &&
      localState.deliveryData &&
      localState.deliveryData.fullName &&
      localState.deliveryData.email &&
      localState.deliveryData.phoneNumber
    );
  }, [cartItems, localState.dietaryData, localState.deliveryData]);

  const convertCartToOrderItems = useCallback(() => {
    // Convert CartSaved items to CartItemReadyToSubmit format directly
    // This avoids dependency on workflow.orderAdapter which might not be initialized
    return cartItems.map((item) => ({
      variant_id: item.variant_id,
      quantity: item.quantity,
      note: item.note,
      product_name: item.product_name,
      sku: item.sku,
      in_cart: item.in_cart,
    }));
  }, [cartItems]);

  const createOrderToSubmit = useCallback(() => {
    if (!isReadyToSubmit()) {
      return null;
    }

    // Create OrderReadyToSubmit format for the adapter
    const orderReadyToSubmit = {
      cart: convertCartToOrderItems(),
      deliveryData: {
        ...localState.deliveryData!,
        contactMethod:
          localState.deliveryData!.contactMethod === 'email' || localState.deliveryData!.contactMethod === 'phone'
            ? localState.deliveryData!.contactMethod
            : ('email' as 'email' | 'phone'),
      },
      dietaryData: localState.dietaryData!,
    };

    return OrderTypeUtils.isValidForSubmission(orderReadyToSubmit) ? orderReadyToSubmit : null;
  }, [isReadyToSubmit, convertCartToOrderItems, localState.deliveryData, localState.dietaryData]);

  const submitOrder = useCallback(async () => {
    const orderData = createOrderToSubmit();
    if (!orderData) {
      throw new Error('Order is not ready to submit');
    }

    // Start the state machine workflow with properly typed OrderReadyToSubmit
    workflow.startOrder(orderData);
  }, [createOrderToSubmit, workflow]);

  const value: OrderContextType = {
    state,
    setDietaryData,
    setDeliveryData,
    setStep,
    submitOrder,
    createOrderToSubmit,
    resetOrder,
    isReadyToSubmit,
  };

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};

export const CleanOrderProvider = OrderProvider;
